import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTokens } from "api/accessTokens";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { OLSwitch } from "components/FormControls/Toggle";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Token } from "types/general";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { useStyles } from "./AdminForm.styles";

export function ManageAccessTokenModal({
  open,
  title,
  submitText,
  initialValues,
  close,
  openTokenModal,
}: {
  open: boolean;
  title: string;
  submitText: string;
  initialValues?: Token;
  close: () => void;
  openTokenModal?: (token: string) => void;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const { createToken, editToken } = useTokens();
  const { notify } = useNotifications();
  const { id: adminId } = useParams<{ id: string }>();
  const isEditMode = !!initialValues;

  const [isExpirationDateChecked, setExpirationDateChecked] = useState(false);

  useEffect(() => {
    if (!!initialValues?.expirationDate) {
      setExpirationDateChecked(true);
    }
  }, [initialValues]);

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      initialValues={{
        name: initialValues?.name || "",
        expirationDate: initialValues?.expirationDate || "",
      }}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});
        try {
          const response =
            isEditMode && adminId
              ? await editToken(adminId, initialValues?.apiKeyId, {
                  apiKey: values,
                })
              : adminId &&
                (await createToken(adminId, {
                  apiKey: values,
                }));

          if (response) {
            if (isEditMode) {
              close();
              notify(message({ id: "editedApiKeyConfirmation" }));
            } else {
              openTokenModal?.(response.token);
              notify(message({ id: "addedApiKeyConfirmation" }));
            }
          }
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          setStatus(errors);
          focusError();
        }
      }}
    >
      {({ handleSubmit, setFieldValue }) => {
        return (
          <OLModal
            open={open}
            onClose={close}
            submitAction={handleSubmit}
            cancelText={message({ id: "cancel" })}
            header={message({ id: title })}
            submitText={message({ id: submitText })}
            className={classes.modal}
            data-test-id="api-key-modal"
          >
            <Form onSubmit={handleSubmit}>
              <TextInput
                name="name"
                width="100%"
                data-test-id="token-name"
                label={<RequiredFieldLabel text={message({ id: "keyName" })} />}
                className={classes.keyNameInput}
              />
              <Box display="flex" alignItems="center" mt={12}>
                <OLSwitch
                  checked={isExpirationDateChecked}
                  data-test-id="set-expiration-date"
                  onChange={(e) => {
                    setExpirationDateChecked(e.target.checked);
                    if (e.target.checked === false) {
                      setFieldValue("expirationDate", "");
                    }
                  }}
                />
                <Typography>{message({ id: "setExpirationDate" })}</Typography>
              </Box>
              {isExpirationDateChecked && (
                <DateTimeInput
                  disablePast
                  name="expirationDate"
                  format="yyyy-MM-dd HH:mmXXX"
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "expirationDate" })}
                    />
                  }
                  data-test-id="token-expiration-date"
                  defaultValue={null}
                  width="100%"
                  className={classes.dateTimeInput}
                  onChange={(e) =>
                    setFieldValue("expirationDate", dayjs(e).format())
                  }
                />
              )}
            </Form>
          </OLModal>
        );
      }}
    </Formik>
  );
}
